import React, { useState, useEffect } from 'react';
import PommeLogo from '../assets/POMME LOGO.svg';

// Styles pour les animations personnalisées
const animationStyles = `
  @keyframes float {
    0% { transform: translateY(0px); }
    50% { transform: translateY(-10px); }
    100% { transform: translateY(0px); }
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  
  @keyframes pulse {
    0% { opacity: 0.3; }
    50% { opacity: 0.6; }
    100% { opacity: 0.3; }
  }
  
  .animate-spin-slow {
    animation: spin 15s linear infinite;
  }
`;

interface LoadingScreenProps {
  onLoadingComplete: () => void;
  minDisplayTime?: number;
  videoLoadingProgress?: number; // Progression réelle du chargement des vidéos (0-100)
}

const LoadingScreen: React.FC<LoadingScreenProps> = ({ 
  onLoadingComplete, 
  minDisplayTime = 3000, // Temps minimum d'affichage en ms
  videoLoadingProgress = 0 // Progression réelle du chargement des vidéos
}) => {
  // Utiliser la progression réelle du chargement des vidéos si disponible, sinon simuler
  const [simulatedProgress, setSimulatedProgress] = useState(0);
  const [isExiting, setIsExiting] = useState(false);
  const [currentText, setCurrentText] = useState(0);
  
  // Calculer la progression finale (max entre la progression réelle et simulée)
  const progress = Math.max(simulatedProgress, videoLoadingProgress);
  
  // Textes minimalistes qui résument le portfolio
  const texts = [
    "SOCIAL MEDIA 3D TEASER",
    "PRODUCT PROMOTION",
    "SCENOGRAPHIE SCREEN ANIMATION",
    "FULL VIDEO CLIP / EDIT",
    "2D VIDEO / FLYER"
  ];
  
  // Effet pour changer le texte périodiquement
  useEffect(() => {
    const textInterval = setInterval(() => {
      setCurrentText(prev => (prev + 1) % texts.length);
    }, 1000);
    
    return () => clearInterval(textInterval);
  }, [texts.length]);
  
  useEffect(() => {
    // Simuler une progression de chargement
    const interval = setInterval(() => {
      setSimulatedProgress((prev: number) => {
        const newProgress = prev + Math.random() * 10;
        return newProgress >= 100 ? 100 : newProgress;
      });
    }, 200);
    
    // Assurer un temps minimum d'affichage
    const timer = setTimeout(() => {
      clearInterval(interval);
      setSimulatedProgress(100);
      
      // Démarrer l'animation de sortie
      setTimeout(() => {
        setIsExiting(true);
        
        // Attendre que l'animation de sortie soit terminée avant de compléter
        setTimeout(() => {
          onLoadingComplete();
        }, 800); // Durée de l'animation de sortie
      }, 500);
    }, minDisplayTime);
    
    return () => {
      clearInterval(interval);
      clearTimeout(timer);
    };
  }, [onLoadingComplete, minDisplayTime]);
  
  return (
    <div 
      className={`fixed inset-0 bg-white flex flex-col items-center justify-center z-50 transition-opacity duration-800 ease-in-out ${isExiting ? 'opacity-0' : 'opacity-100'}`}
      style={{ transitionDuration: '800ms' }}
    >
      <style>{animationStyles}</style>
      <div className="flex flex-col items-center">
        <div className="relative mb-6">
          <img 
            src={PommeLogo} 
            alt="POMME Logo" 
            className="w-32 h-32 animate-spin-slow" 
            style={{
              animation: 'float 3s ease-in-out infinite, spin 15s linear infinite',
              transformOrigin: 'center center',
            }}
          />
          <div 
            className="absolute inset-0 bg-white bg-opacity-0 rounded-full" 
            style={{
              boxShadow: '0 0 40px 20px rgba(255, 255, 255, 0.8)',
              animation: 'pulse 2s ease-in-out infinite',
            }}
          />
        </div>
        <h1 className="text-3xl font-light mb-2 text-black">Noah Desmoulieres</h1>
        
        {/* Texte descriptif qui change */}
        <div className="h-8 mb-6 text-center">
          <p className="text-gray-700 text-sm font-light tracking-wider">{texts[currentText]}</p>
        </div>
        
        {/* Barre de progression */}
        <div className="w-64 h-1 bg-gray-200 rounded-full overflow-hidden mb-2">
          <div 
            className="h-full bg-black transition-all duration-300 ease-out"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
        
        <p className="text-gray-500 text-sm">{Math.round(progress)}%</p>
      </div>
    </div>
  );
};

export default LoadingScreen;
